import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Modal, ModalBody, ModalHeader, ModalFooter, Button, Spinner, Alert, Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import api from '../helpers/api';
import usePermission from '../Hooks/usePermission';
import { formatDateForAPI } from '../constants/utils';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const AssetStatusChangeModal = ({ isOpen, toggle, asset, onSuccess, workOrderId }) => {
  const { t } = useTranslation();
  const { isAdmin } = usePermission();

  const STATUS_OPTIONS = [
    { value: 'ONLINE', label: t('shared.online', 'Online'), color: '#10b981' },
    { value: 'OFFLINE', label: t('shared.offline', 'Offline'), color: '#ef4444' },
    { value: 'DO_NOT_TRACK', label: t('shared.do_not_track', 'Do Not Track'), color: '#6b7280' },
    ...(isAdmin ? [{ value: 'DECOMMISSIONED', label: t('shared.decommissioned', 'Decommissioned'), color: '#475569' }] : []),
  ];

  const currentStatus = (asset?.assetStatus || '').toUpperCase();
  const isCurrentlyDecommissioned = currentStatus === 'DECOMMISSIONED';
  // Non-admins viewing a decommissioned asset cannot change its status at all.
  const reviveBlocked = isCurrentlyDecommissioned && !isAdmin;

  const [selectedTopLevel, setSelectedTopLevel] = useState(null);
  const [selectedDowntimeReasonId, setSelectedDowntimeReasonId] = useState(null);
  const [decommissionedReason, setDecommissionedReason] = useState('');
  const [decommissionedOn, setDecommissionedOn] = useState(todayIso());
  const [downtimeReasons, setDowntimeReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch downtime reasons from API
  useEffect(() => {
    if (isOpen) {
      setLoadingReasons(true);
      api.get('asset/downtime-reason')
        .then(res => {
          setDowntimeReasons(res.data?.response || []);
        })
        .catch(() => {
          setDowntimeReasons([]);
        })
        .finally(() => setLoadingReasons(false));
    }
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const resetState = useCallback(() => {
    setSelectedTopLevel(null);
    setSelectedDowntimeReasonId(null);
    setDecommissionedReason('');
    setDecommissionedOn(todayIso());
    setNotes('');
    setShowNotes(false);
    setError(null);
    setDropdownOpen(false);
    setConfirmOpen(false);
  }, []);

  const handleToggle = () => {
    resetState();
    toggle();
  };

  const handleTopLevelSelect = (value) => {
    setSelectedTopLevel(value);
    setSelectedDowntimeReasonId(null);
    setDropdownOpen(false);
    setError(null);
    if (value === 'DECOMMISSIONED') {
      // Default the date when entering the panel; reason stays empty until typed.
      if (!decommissionedOn) setDecommissionedOn(todayIso());
    }
  };

  const isReviveAttempt = isCurrentlyDecommissioned
    && selectedTopLevel
    && selectedTopLevel !== 'DECOMMISSIONED';
  const isDecommissionAttempt = selectedTopLevel === 'DECOMMISSIONED'
    && !isCurrentlyDecommissioned;

  const canConfirm = () => {
    if (!selectedTopLevel) return false;
    if (selectedTopLevel === 'OFFLINE' && !selectedDowntimeReasonId) return false;
    if (selectedTopLevel === 'DECOMMISSIONED' && !decommissionedReason.trim()) return false;
    return true;
  };

  const performUpdate = async () => {
    setLoading(true);
    setError(null);

    try {
      await api.put('asset/update-status', {
        assetId: asset.id,
        status: selectedTopLevel,
        downtimeReasonId: selectedTopLevel === 'OFFLINE' ? selectedDowntimeReasonId : null,
        notes: notes.trim() || null,
        decommissionedReason: selectedTopLevel === 'DECOMMISSIONED' ? decommissionedReason.trim() : null,
        decommissionedOn: selectedTopLevel === 'DECOMMISSIONED' ? (formatDateForAPI(decommissionedOn) || null) : null,
        // Stamp the downtime row with the WO context (when launched from a WO detail screen),
        // so its cost contributes to the WO total. Null in all other launch contexts.
        workOrderId: workOrderId ?? null,
      });
      resetState();
      toggle();
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.message || t('business.components.status_modal.update_failed', 'Failed to update status'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!canConfirm()) return;
    if (!asset?.id) {
      setError(t('shared.asset_id_missing', 'Asset ID is missing. Please close and try again.'));
      return;
    }
    if (isDecommissionAttempt || isReviveAttempt) {
      setConfirmOpen(true);
      return;
    }
    await performUpdate();
  };

  const selectedOpt = STATUS_OPTIONS.find(o => o.value === selectedTopLevel);

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered size="md">
      <ModalHeader toggle={handleToggle}>
        {t('shared.buttons_change_status', 'Change Status')} {asset?.assetName ? `— ${asset.assetName}` : ''}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger" className="mb-3">{error}</Alert>}

        {/* Status dropdown */}
        <div className="mb-3" ref={dropdownRef} style={{ position: 'relative' }}>
          <label className="form-label fw-medium">{t('shared.status', 'Status')} <span className="text-muted fw-normal">({t('shared.required_label', 'required')})</span></label>
          <div
            className="form-select d-flex align-items-center"
            style={{ cursor: 'pointer' }}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {selectedOpt ? (
              <span className="d-flex align-items-center gap-2">
                <span className="rounded-circle" style={{ width: 10, height: 10, backgroundColor: selectedOpt.color }} />
                {selectedOpt.label}
              </span>
            ) : (
              <span className="text-muted">{t('business.components.status_modal.select_status', 'Select status')}</span>
            )}
          </div>
          {dropdownOpen && (
            <div
              className="border rounded shadow-sm bg-white"
              style={{ position: 'absolute', zIndex: 1050, width: '100%', marginTop: 2 }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  className={`d-flex align-items-center gap-2 px-3 py-2 ${selectedTopLevel === opt.value ? 'bg-light' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleTopLevelSelect(opt.value)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedTopLevel === opt.value ? '#f8f9fa' : ''}
                >
                  <span className="rounded-circle flex-shrink-0" style={{ width: 10, height: 10, backgroundColor: opt.color }} />
                  <span>{opt.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Revive blocked notice (non-admin viewing a decommissioned asset) */}
        {reviveBlocked && (
          <Alert color="warning" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
            {t('business.assets.errors.revive_admin_only', 'Only an Administrator or Super Admin can revive a decommissioned asset.')}
          </Alert>
        )}

        {/* Decommission warning + fields */}
        {selectedTopLevel === 'DECOMMISSIONED' && (
          <>
            <Alert color="warning" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
              {t('shared.decommission_warning_hidden_from_list', 'This asset will be hidden from the standard asset list. Only Administrators can revive it.')}
            </Alert>
            <div className="mb-3">
              <label className="form-label fw-medium">
                {t('business.assets.decommission_reason', 'Decommission reason')} <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                maxLength={500}
                value={decommissionedReason}
                onChange={(e) => setDecommissionedReason(e.target.value)}
                placeholder={t('business.assets.decommission_reason_helper', 'Briefly describe why this asset is being decommissioned (e.g. sold, scrapped, replaced).')}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-medium">
                {t('business.assets.decommissioned_on', 'Decommissioned on')}
              </label>
              <Input
                type="date"
                value={decommissionedOn}
                onChange={(e) => setDecommissionedOn(e.target.value)}
              />
            </div>
          </>
        )}

        {/* Downtime reason sub-selector */}
        {selectedTopLevel === 'OFFLINE' && (
          <div className="mb-3">
            <label className="form-label fw-medium">{t('shared.downtime_reason', 'Downtime Reason')} <span className="text-muted fw-normal">({t('shared.required_label', 'Required')})</span></label>
            <Input
              type="select"
              value={selectedDowntimeReasonId || ''}
              onChange={(e) => { setSelectedDowntimeReasonId(e.target.value ? Number(e.target.value) : null); setError(null); }}
              className={!selectedDowntimeReasonId ? 'text-muted' : ''}
              disabled={loadingReasons}
            >
              <option value="">{loadingReasons ? t('shared.loading', 'Loading...') : t('business.components.status_modal.select_reason', 'Select downtime reason')}</option>
              {downtimeReasons.map((reason) => {
                const label = t(`shared.${reason.label.toLowerCase().replace(/\s+/g, '_')}`, reason.label);
                const description = reason.description
                  ? t(`shared.downtime_${(reason.name || '').toLowerCase()}_desc`, reason.description)
                  : '';
                return (
                  <option key={reason.id} value={reason.id}>
                    {label}{description ? ` — ${description}` : ''}
                  </option>
                );
              })}
            </Input>
          </div>
        )}

        {/* Notes */}
        {!showNotes ? (
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none mb-3"
            onClick={() => setShowNotes(true)}
          >
            + {t('business.components.status_modal.add_notes', 'Add notes')}
          </button>
        ) : (
          <div className="mb-3">
            <label className="form-label fw-medium">{t('shared.notes', 'Notes')}</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder={t('business.components.status_modal.notes_placeholder', 'Add notes about this status change...')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        )}

        {/* Actions */}
        <div className="d-flex justify-content-end gap-2">
          <Button color="light" onClick={handleToggle} disabled={loading}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={!canConfirm() || loading || reviveBlocked}
          >
            {loading ? <Spinner size="sm" className="me-1" /> : null}
            {t('shared.button_confirm', 'Confirm')}
          </Button>
        </div>
      </ModalBody>

      {/* Secondary confirmation for decommission / revive — consequence-disclosing dialog. */}
      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} centered size="sm">
        <ModalHeader toggle={() => setConfirmOpen(false)}>
          {isDecommissionAttempt
            ? t('business.assets.confirm_decommission_title', 'Decommission this asset?')
            : t('business.assets.revive_asset_title', 'Revive this asset?')}
        </ModalHeader>
        <ModalBody>
          {isDecommissionAttempt
            ? t('business.assets.confirm_decommission_body', 'The asset will be hidden from the standard list and excluded from dashboard counts. Only an Administrator can revive it. Continue?')
            : t('business.assets.revive_asset_body', 'The decommission reason and date will be cleared. The change is recorded in the activity log. Continue?')}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setConfirmOpen(false)} disabled={loading}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button color="primary" onClick={async () => { setConfirmOpen(false); await performUpdate(); }} disabled={loading}>
            {loading ? <Spinner size="sm" className="me-1" /> : null}
            {t('shared.button_confirm', 'Confirm')}
          </Button>
        </ModalFooter>
      </Modal>
    </Modal>
  );
};

export default AssetStatusChangeModal;
